import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Wind, Lightbulb, Bed, Droplet, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import logo from "../assets/hospitalimg.png";
import CountUp from 'react-countup';
import icu from "../assets/ICU.png";
import ward from "../assets/specialward.png";
import ot from "../assets/OT.png";
import coridoor from "../assets/coridoor.JPG";

const Infrastructure = () => {
  const facilities = [
    {
      title: 'Wide Corridors',
      description: 'Spacious corridors designed for easy movement of patients, equipment, and staff',
      // image: 'https://images.unsplash.com/photo-1629410484397-a4dcd74088a0',
      image: coridoor,
      icon: Building2,
      features: ['Easy Navigation', 'Wheelchair Accessible', 'Well-lit Pathways', 'Safety Rails'],
    },
    {
      title: 'Good Light & Fresh Air',
      description: 'Natural lighting and proper ventilation throughout the facility for a healthy environment',
      image: 'https://images.pexels.com/photos/19921278/pexels-photo-19921278.jpeg',
      icon: Wind,
      features: ['Natural Lighting', 'Cross Ventilation', 'Air Quality Control', 'Energy Efficient'],
    },
    {
      title: 'Spacious Wards',
      description: 'Comfortable and well-maintained wards with adequate space for patient care',
      image: ward,
      icon: Bed,
      features: ['Patient Comfort', 'Privacy Curtains', 'Call Button System', 'Visitor Seating'],
    },
    {
      title: 'ICU (Intensive Care Unit)',
      description: 'Private rooms equipped with modern amenities for personalized patient care',
      image: icu,
      icon: Shield,
      features: ['24/7 Support', 'Clean & Quite', 'Moden Equipment', 'Premium Care'],
    },
    {
      title: 'State Of The Art Equipment',
      description: 'State-of-the-art medical equipment for accurate diagnosis and effective treatment',
      image: 'https://images.unsplash.com/photo-1513224502586-d1e602410265',
      icon: Lightbulb,
      features: ['Latest Technology', 'Regular Maintenance', 'Digital Systems', 'Advanced Monitoring'],
    },
    {
      title: 'OT (Operation Theatre)',
      description: 'Strict hygiene protocols and regular sanitation to ensure a safe environment',
      image: ot,
      icon: Droplet,
      features: ['Daily Sanitization', 'Infection Control', 'Waste Management', 'Clean Environment'],
    },
  ];

  const highlights = [
  { number: 100, suffix: '+', label: 'Bed Capacity' },
  { number: 5, suffix: '+', label: 'Special Rooms' },
  { number: 24, suffix: '/7', label: 'Power Backup' },
  { number: 100, suffix: '%', label: 'Hygiene Standards' },
];


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <img
          src={logo}
          alt="Infrastructure"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-sky-900/90 to-blue-900/70"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold text-white mb-4"
          >
            Our <span className="text-sky-300">Infrastructure</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-white/90 max-w-2xl mx-auto"
          >
            Modern facilities designed with patient comfort and safety in mind
          </motion.p>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-12 bg-gradient-to-r from-sky-500 to-blue-600 text-white">
  <div className="container mx-auto px-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
      {highlights.map((item, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          className="text-center"
        >
          <h3 className="text-4xl md:text-5xl font-bold mb-2">
            <CountUp
              start={0}
              end={item.number}
              duration={2}
              delay={0.2}
            />
            {item.suffix}
          </h3>
          <p className="text-lg text-white/90">{item.label}</p>
        </motion.div>
      ))}
    </div>
  </div>
</section>


      {/* Facilities Details */}
      <section className="py-20 bg-gradient-to-b from-white to-sky-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800">
              World-Class <span className="text-sky-500">Facilities</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our hospital is built with patient comfort, safety, and modern healthcare delivery in mind
            </p>
          </motion.div>

          <div className="space-y-20">
            {facilities.map((facility, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Image Section */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="relative group">
                      <div className="absolute -inset-4 bg-gradient-to-r from-sky-400 to-blue-500 rounded-3xl opacity-20 group-hover:opacity-30 transition-opacity blur-xl"></div>
                      <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                        <img
                          src={facility.image}
                          alt={facility.title}
                          className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                        <div className="absolute bottom-6 left-6">
                          <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center">
                            <facility.icon className="w-8 h-8 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <motion.div
                      initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <h2 className="text-4xl font-bold mb-6 text-gray-800">{facility.title}</h2>
                      <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                        {facility.description}
                      </p>

                      <div className="grid grid-cols-2 gap-4">
                        {facility.features.map((feature, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                            className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all"
                          >
                            <div className="w-2 h-2 bg-sky-500 rounded-full"></div>
                            <span className="text-gray-700 font-medium text-sm">{feature}</span>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-sky-500 to-blue-600 rounded-3xl p-12 text-white text-center shadow-2xl"
          >
            <h2 className="text-4xl font-bold mb-4">Visit Our Facility</h2>
            <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
              Experience our world-class infrastructure and patient care facilities firsthand
            </p>
            <a href="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-sky-600 px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all"
              >
                Contact Us for a Tour
              </motion.button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Infrastructure;